/**
 * VedangJyotisham — Invitation-Only Onboarding System Type Definitions
 * Decision ID: VJ-PRODUCT-INVITATION-ONLY-001 (Option C: Existing Guest Experience Preserved)
 * Governing Specification: docs/VJ_PRODUCT_INVITATION_ONLY_001_SPEC.md (v2.1.0-final)
 */

export enum InvitationStatus {
  REQUESTED = 'REQUESTED',                     // Step 1: Form submitted; awaiting email verification OTP
  EMAIL_VERIFIED = 'EMAIL_VERIFIED',           // Step 1 Complete: Email ownership verified
  PENDING_REVIEW = 'PENDING_REVIEW',           // Step 2: Enqueued in Super Admin review queue
  APPROVED = 'APPROVED',                       // Step 2 Complete: Super Admin approved; tier assigned
  DECLINED = 'DECLINED',                       // Step 2 Terminal: Super Admin declined application
  INVITATION_ISSUED = 'INVITATION_ISSUED',     // Step 3: Cryptographic token generated, hashed & dispatched
  INVITATION_RESERVED = 'INVITATION_RESERVED', // Step 4 (In-Flight): 60s mutex lock during registration
  INVITATION_REDEEMED = 'INVITATION_REDEEMED', // Step 4 Complete: Account created; token consumed (Terminal)
  INVITATION_EXPIRED = 'INVITATION_EXPIRED',   // Timeout Terminal: 7-day issuance window elapsed
  INVITATION_REVOKED = 'INVITATION_REVOKED',   // Admin Terminal: Manually invalidated prior to redemption
}

export type DeliveryStatus =
  | 'QUEUED'
  | 'SUBMITTED'
  | 'ACCEPTED_BY_PROVIDER'
  | 'SENT'
  | 'DELIVERED'
  | 'DELIVERY_FAILED'
  | 'EXPIRED'
  | 'REDEEMED'
  | 'REVOKED';

export type IntendedUsage = 'seeker' | 'astrologer';
export type AllocatedTier = 'BASIC' | 'PRO_ASTRO';

export interface FirestoreInvitationRequestDoc {
  requestId: string;                         // Document ID: req_<16_bytes_hex>
  fullName: string;                          // Trimmed, sanitized applicant name
  email: string;                             // Normalized lowercase email
  intendedUsage: IntendedUsage;              // Declared intent
  reasonForJoining?: string;                 // Optional statement (max 500 chars)
  referralCode?: string;                     // Optional referral code
  status: InvitationStatus;                  // Current lifecycle state

  // Email Verification Sub-Record (Step 1)
  emailVerificationTokenHash: string;        // Server-peppered HMAC-SHA256 (salt:mac) of 6-digit OTP
  emailVerificationExpiresAt: number;        // Epoch millis (15-minute TTL)
  emailVerifiedAt?: string;                  // ISO 8601 timestamp
  verificationAttempts: number;              // Attempt counter (max 3 before lockout)
  resendCooldownUntil?: number;              // Epoch millis (exponential cooldown on failures)

  // Administrative Review Sub-Record (Step 2)
  reviewedBy?: string;                       // Authorized super admin email
  reviewedAt?: string;                       // ISO 8601 timestamp
  declineReason?: string;                    // Reason if declined

  // Issuance Sub-Record (Step 3)
  invitationTokenHash?: string;              // SHA-256 of 256-bit secret token
  invitationTokenPrefix?: string;            // First 8 characters for admin UI audit display (e.g. "vj_inv_a1b2c3d4")
  invitationExpiresAt?: number;              // Epoch millis (7-day default TTL)
  issuedAt?: string;                         // ISO 8601 timestamp
  allocatedTier?: AllocatedTier;             // Provisioned tier (default: BASIC)

  // Delivery Tracking (Section 3 Mandatory Capability)
  deliveryStatus?: DeliveryStatus;           // Status of email transmission
  lastSentAt?: string;                       // ISO timestamp of email dispatch
  deliveryError?: string;                    // Error details if delivery failed
  emailMessageId?: string;                   // External provider message ID

  // Redemption Sub-Record (Step 4)
  redeemedAt?: string;                       // ISO 8601 timestamp
  redeemedUserId?: string;                   // Linked RegisteredUser.uid
  redeemedIpHash?: string;                   // SHA-256 of client IP

  // Audit & Retention Metadata
  createdAt: string;                         // ISO 8601 timestamp
  updatedAt: string;                         // ISO 8601 timestamp
  requestIpHash: string;                     // SHA-256 of requesting client IP
  ttlExpiresAt?: any;                        // Native Firestore TTL auto-pruning timestamp
}

export interface FirestoreInvitationTokenDoc {
  tokenHash: string;                         // Document ID: SHA-256 of raw secret token
  requestId: string;                         // Foreign key to invitation_requests
  boundEmail: string;                        // Lowercase applicant email (INV-INVITE-05)
  allocatedTier: AllocatedTier;              // Provisioned tier
  status: 'ISSUED' | 'RESERVED' | 'REDEEMED' | 'EXPIRED' | 'REVOKED';
  version: number;                           // Optimistic concurrency version counter
  expiresAt: number;                         // Epoch millis (7-day TTL)

  // In-Flight Reservation Lock (Two-Phase Commit Phase 1)
  reservationId?: string;                    // UUID of active in-flight registration attempt
  reservedAt?: number;                       // Epoch millis
  reservationExpiresAt?: number;             // Epoch millis (60-second TTL)

  // Terminal Redemption Metadata (Two-Phase Commit Phase 2)
  redeemedAt?: number;                       // Epoch millis
  redeemedUserId?: string;                   // Resulting user UID

  createdAt: number;                         // Epoch millis
  updatedAt: number;                         // Epoch millis
  ttlExpiresAt?: any;                        // Native Firestore TTL auto-pruning timestamp
}

export interface OAuthQuarantineRecord {
  uid: string;                               // Firebase Auth UID (Document ID)
  email: string;                             // Google email address
  providerId: 'google.com';                  // Must strictly be 'google.com'
  creationTime: string;                      // ISO timestamp from Firebase Auth metadata
  detectedAt: number;                        // Epoch millis when first detected
  quarantineExpiresAt: number;               // Epoch millis (detectedAt + 24h)
  status: 'STAGED_FOR_DELETION' | 'PURGED' | 'RECLAIMED';
  stageCycle: number;                        // Must reach 2 before purge eligibility
  reason?: string;                           // Rejection or staging reason
  firstProbeError?: string;                  // Last probe status
  reclaimedAt?: string;                      // ISO timestamp if user subsequently redeemed token
  purgedAt?: string;                         // ISO timestamp of admin.auth().deleteUser
  deletionAuditId?: string;                  // Foreign key to /auditLogs/{auditId}
  serverInstanceId: string;                  // Container instance ID that processed record
  ttlExpiresAt?: any;                        // Auto-prune record 30 days post-purge
}

// Client API Payloads & DTOs
export interface InvitationRequestPayload {
  fullName: string;
  email: string;
  intendedUsage: IntendedUsage;
  reasonForJoining?: string;
  referralCode?: string;
}

export interface VerifyEmailOtpPayload {
  requestId: string;
  email: string;
  otp: string;
}

export interface ValidateTokenResponse {
  valid: boolean;
  boundEmail?: string;
  allocatedTier?: AllocatedTier;
  expiresAt?: number;
  reason?: string;
}

export interface PublicInvitationConfig {
  invitationOnlyEnabled: boolean;
  registrationOpen: boolean;
  minOtpCooldownSeconds: number;
}

